import { createContext, useEffect, useMemo } from 'react';
import { useNullableContext } from 'shared/hooks/useNullableContext';
import { UpdateParams, useLocationParams } from 'shared/routing/LocationParams';
import { useFlag } from 'shared/settings/FeatureToggles';
import { useDelayedLoadsCount, useDriversCount } from './HomeAPI';
import { HomePageParams, homePageParamsSchema } from './HomeDTO';

const HomeContext = createContext<HomeContextType | null>(null);

interface HomeContextType {
  isDriversLoading: boolean;
  isDelayedLoadsLoading: boolean;
  isDispatchQueueEnabled: boolean;
  deliveryCount: number;
  pickupCount: number;
  unassignedCount: number;
  availableCount: number;
  assignedCount: number;
  deliveringCount: number;
  totalDriversCount: number;
  totalDelayedLoadsCount: number;
  driversTabs: Array<{ label: string; count: number; stage: string }>;
  delayedLoadsTabs: Array<{ label: string; count: number; stage: string }>;
  params: HomePageParams;
  updateParams: UpdateParams<HomePageParams>;
}

export const useHomeContext = () => {
  return useNullableContext('HomeContext', HomeContext);
};

interface HomeTab {
  stage: string;
  count: number;
}

type DefaultParamsUpdate = Partial<
  Pick<HomePageParams, 'driver_status' | 'delayed_load_stage'>
>;

export function selectDefaultTab<T extends HomeTab>(
  tabs: readonly T[],
): T | undefined {
  return tabs.find(({ count }) => !!count);
}

export function getDefaultHomeParamsUpdate({
  isDriversLoading,
  isDelayedLoadsLoading,
  driversTabs,
  delayedLoadsTabs,
  params,
}: {
  isDriversLoading: boolean;
  isDelayedLoadsLoading: boolean;
  driversTabs: readonly HomeTab[];
  delayedLoadsTabs: readonly HomeTab[];
  params: Pick<HomePageParams, 'driver_status' | 'delayed_load_stage'>;
}): DefaultParamsUpdate | null {
  if (isDriversLoading || isDelayedLoadsLoading) {
    return null;
  }

  const defaultDriverStage = selectDefaultTab(driversTabs);
  const defaultDelayedLoadStage = selectDefaultTab(delayedLoadsTabs);

  if (
    params.driver_status === defaultDriverStage?.stage ||
    params.delayed_load_stage === defaultDelayedLoadStage?.stage
  ) {
    return null;
  }

  if (!defaultDriverStage && !defaultDelayedLoadStage) {
    return null;
  }

  return {
    ...(defaultDriverStage && { driver_status: defaultDriverStage.stage }),
    ...(defaultDelayedLoadStage && {
      delayed_load_stage: defaultDelayedLoadStage.stage,
    }),
  };
}

export const HomeProvider = ({ children }: { children: React.ReactNode }) => {
  const [params, updateParams] = useLocationParams({
    zodSchema: homePageParamsSchema,
    persistName: 'Dashboard',
  });

  const isDispatchQueueEnabled = useFlag('ctms_homepage');

  const { data: driversCount, isLoading: isDriversLoading } = useDriversCount();
  const { data: delayedLoadsCount, isLoading: isDelayedLoadsLoading } =
    useDelayedLoadsCount();

  const deliveryCount = delayedLoadsCount?.delivery ?? 0;
  const pickupCount = delayedLoadsCount?.pickup ?? 0;
  const unassignedCount = delayedLoadsCount?.unassigned ?? 0;
  const totalDelayedLoadsCount =
    deliveryCount +
    pickupCount +
    (isDispatchQueueEnabled ? unassignedCount : 0);

  const availableCount = driversCount?.available ?? 0;
  const assignedCount = driversCount?.assigned ?? 0;
  const deliveringCount = driversCount?.delivering ?? 0;
  const totalDriversCount = driversCount?.total ?? 0;

  const driversTabs = useMemo(() => {
    return [
      { label: 'Available', count: availableCount, stage: 'available' },
      { label: 'Assigned', count: assignedCount, stage: 'assigned' },
      { label: 'Delivering', count: deliveringCount, stage: 'delivering' },
    ];
  }, [availableCount, assignedCount, deliveringCount]);

  const delayedLoadsTabs = useMemo(() => {
    if (isDispatchQueueEnabled) {
      return [
        { label: 'Unassigned', count: unassignedCount, stage: 'unassigned' },
        { label: 'Late Pickups', count: pickupCount, stage: 'pickup' },
        { label: 'Late Deliveries', count: deliveryCount, stage: 'delivery' },
      ];
    }

    return [
      { label: 'Delayed Pickups', count: pickupCount, stage: 'pickup' },
      { label: 'Delayed Deliveries', count: deliveryCount, stage: 'delivery' },
    ];
  }, [isDispatchQueueEnabled, unassignedCount, pickupCount, deliveryCount]);

  useEffect(() => {
    const update = getDefaultHomeParamsUpdate({
      isDriversLoading,
      isDelayedLoadsLoading,
      driversTabs,
      delayedLoadsTabs,
      params,
    });

    if (update) {
      updateParams((prev) => ({ ...prev, ...update }));
    }
    // Only re-render when the counts change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driversTabs, delayedLoadsTabs]);

  const ctx = useMemo<HomeContextType>(() => {
    return {
      params,
      driversTabs,
      delayedLoadsTabs,
      isDriversLoading,
      isDelayedLoadsLoading,
      isDispatchQueueEnabled,
      deliveryCount,
      pickupCount,
      unassignedCount,
      availableCount,
      assignedCount,
      deliveringCount,
      updateParams,
      totalDriversCount,
      totalDelayedLoadsCount,
    };
  }, [
    params,
    driversTabs,
    delayedLoadsTabs,
    updateParams,
    totalDriversCount,
    totalDelayedLoadsCount,
    isDriversLoading,
    isDelayedLoadsLoading,
    isDispatchQueueEnabled,
    deliveryCount,
    pickupCount,
    unassignedCount,
    availableCount,
    assignedCount,
    deliveringCount,
  ]);

  return <HomeContext.Provider value={ctx}>{children}</HomeContext.Provider>;
};
